import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCulturesByExploitationIdForSimulation } from 'services/API/Exploitation';
import { getConstraintCategories } from 'services/API/Cooperative';
import { getSimulationResult, getSimulationStatus, simulate } from 'services/API/Simulation';
import { toast } from 'react-toastify';
import { sendEvent } from 'utils/Event';
import { sortWithLocale } from 'utils/tools_functions';
import { buildRecapData } from './Settings/Utils/SharedFunctions';
import i18next from 'i18n';
export const STORAGE_KEY = 'simulations_data';

const encodeData = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    return btoa(encodeURIComponent(jsonString));
  } catch (error) {
    console.error('Erreur encodage données', error);
    return null;
  }
};

export const decodeData = (encodedData) => {
  try {
    const jsonString = decodeURIComponent(atob(encodedData));
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Erreur décodage données', error);
    return null;
  }
};

export const getAllSimulations = () => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (!storedData) return {};

    const parsed = JSON.parse(storedData);
    if (parsed?.data) {
      return decodeData(parsed.data) || {};
    }
    return {};
  } catch (error) {
    console.error('Erreur lecture localStorage', error);
    return {};
  }
};

export const saveAllSimulations = (allSimulations) => {
  try {
    const encodedData = encodeData(allSimulations);
    if (encodedData) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: 1,
          updatedAt: new Date().toISOString(),
          data: encodedData,
        }),
      );
    }
  } catch (error) {
    toast.warning(i18next.t('storage.tooManySimulation'));
    console.error('Erreur sauvegarde localStorage', error);
  }
};

const useSimulationConfig = (
  exploitationSelected,
  onSimulationComplete,
  setIsSimulationContextUpdated,
) => {
  const { t } = useTranslation();
  const [selectedParcels, setSelectedParcels] = useState([]);
  const [culturesResult, setCulturesResult] = useState([]);
  const [selectedCultures, setSelectedCultures] = useState([]);
  const [cultureMinMax, setCultureMinMax] = useState({});
  const [selectedConstraints, setSelectedConstraints] = useState([]);
  const [constraintValues, setConstraintValues] = useState({});
  const [selectedStrategies, setSelectedStrategies] = useState([]);
  const [yearsToSimulate, setYearsToSimulate] = useState(5);
  const [parcelConstraints, setParcelConstraints] = useState([]);

  const [showConstraintModal, setShowConstraintModal] = useState(false);
  const [currentConstraint, setCurrentConstraint] = useState({});

  const [parcelSearchTerm, setParcelSearchTerm] = useState('');
  const [cultureSearchTerm, setCultureSearchTerm] = useState('');
  const [viewMapParcels, setViewMapParcels] = useState(false);
  const [mapCoordinates, setMapCoordinates] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [coProducts, setCoProducts] = useState([]);
  const [hasExistingSimulation, setHasExistingSimulation] = useState(true);
  const [simulationError, setSimulationError] = useState(null);
  const [jobId, setJobId] = useState(null);

  const forecastCampaign = exploitationSelected?.forecastCampaign;

  const fixedCultureIds = useMemo(() => {
    if (!exploitationSelected?.parcels || !selectedParcels?.length || !forecastCampaign) return [];
    const campaignKey = `${forecastCampaign.startYear}-${forecastCampaign.endYear}`;
    const fixedIds = new Set();

    exploitationSelected.parcels
      .filter((p) => selectedParcels.includes(p.id))
      .forEach((parcel) => {
        const culture = parcel.parcelCultures?.[campaignKey];
        if (culture?.isFixed) {
          fixedIds.add(culture.id);
        }
      });

    return [...fixedIds];
  }, [exploitationSelected?.parcels, selectedParcels, forecastCampaign]);

  useEffect(() => {
    if (!fixedCultureIds.length) return;
    const missingFixed = fixedCultureIds.filter((id) => !selectedCultures.includes(id));
    if (missingFixed.length > 0) {
      setSelectedCultures((prev) => [...prev, ...missingFixed]);
    }
  }, [fixedCultureIds]);

  useEffect(() => {
    if (exploitationSelected?.id) {
      if (exploitationSelected?.parcels.find((parcel) => parcel.coordinates.length > 0)) {
        setMapCoordinates(true);
        setViewMapParcels(true);
      }
      loadData();
      loadCoProducts();
    }
  }, [exploitationSelected?.id]);

  useEffect(() => {
    if (exploitationSelected && !hasExistingSimulation) {
      if (culturesResult?.length > 0) {
        const enabledCultureIds = culturesResult
          .filter((c) => c.firstSimulateEnabled)
          .map((c) => c.id);
        setSelectedCultures(enabledCultureIds);

        const initialMinMax = {};
        enabledCultureIds.forEach((id) => {
          initialMinMax[id] = { min: 0, max: 0, unit: 'ha' };
        });
        setCultureMinMax(initialMinMax);
      }
      setSelectedParcels(exploitationSelected.parcels.map((parcel) => parcel.id));
      setSelectedStrategies(['HISTORY']);
      setYearsToSimulate(3);
    }
  }, [culturesResult, hasExistingSimulation]);

  useEffect(() => {
    if (parcelConstraints.length === 0) return;

    const lastValidYear = forecastCampaign?.startYear
      ? forecastCampaign.startYear + yearsToSimulate
      : null;

    const cleaned = parcelConstraints
      .map((constraint) => ({
        ...constraint,
        parcels: constraint.parcels?.filter((id) => selectedParcels.includes(id)) ?? [],
        cultures:
          constraint.constraint === 'conversion'
            ? constraint.cultures
            : (constraint.cultures?.filter((id) => selectedCultures.includes(id)) ?? []),
        years: lastValidYear
          ? (constraint.years?.filter((year) => year <= lastValidYear) ?? [])
          : constraint.years,
      }))
      .filter((constraint) => {
        if (constraint.constraint === 'conversion') {
          return constraint.parcels.length > 0 && constraint.years.length > 0;
        }
        return (
          constraint.parcels.length > 0 &&
          constraint.cultures.length > 0 &&
          constraint.years.length > 0
        );
      });

    if (
      cleaned.length === parcelConstraints.length &&
      cleaned.every(
        (c, i) =>
          c.parcels.length === parcelConstraints[i]?.parcels?.length &&
          c.cultures?.length === parcelConstraints[i]?.cultures?.length &&
          c.years?.length === parcelConstraints[i]?.years?.length,
      )
    ) {
      // If nothing change no update on the constraints
      return;
    }

    const constraintIsDeleted = cleaned.length < parcelConstraints.length;
    toast.warning(
      constraintIsDeleted
        ? t('simulation.messages.parcelConstraintRemoved')
        : t('simulation.messages.parcelConstraintUpdated'),
    );
    setParcelConstraints(cleaned);
  }, [selectedParcels, selectedCultures, yearsToSimulate]);

  const loadData = async () => {
    const result = await getCulturesByExploitationIdForSimulation(exploitationSelected.id);
    setCulturesResult(result.data);
    setIsSimulationContextUpdated(true);
  };

  const loadCoProducts = async () => {
    try {
      const cooperativeId = localStorage.getItem('cooperative');
      if (!cooperativeId) return;

      const response = await getConstraintCategories(cooperativeId);
      const data = response?.data?.data || response?.data || [];

      if (Array.isArray(data)) {
        const besoinsAnimaux = data.find((cat) => cat.name === 'Besoins animaux');
        if (besoinsAnimaux?.coProducts) {
          setCoProducts(besoinsAnimaux.coProducts);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des co-produits:', error);
    }
  };

  const saveToLocalStorage = (simulationConfig, result, jobId = null) => {
    if (!exploitationSelected?.id) return;
    try {
      const allSimulations = getAllSimulations();
      const exploitationId = exploitationSelected.id;

      if (!allSimulations[exploitationId]) {
        allSimulations[exploitationId] = {
          exploitationId,
          exploitationName: exploitationSelected.name,
          current: null,
          history: [],
        };
      }

      const exploitationData = allSimulations[exploitationId];
      const resolvedJobId = jobId || `simu_${Date.now()}`;

      const existsInHistory = exploitationData.history?.some(
        (entry) => entry.jobId === resolvedJobId,
      );
      const existsAsCurrent = exploitationData.current?.jobId === resolvedJobId;
      if (existsInHistory || existsAsCurrent) return;

      if (exploitationData.current?.params) {
        exploitationData.history = exploitationData.history || [];
        exploitationData.history.push(exploitationData.current);
      }

      exploitationData.current = {
        jobId: resolvedJobId,
        params: simulationConfig,
        result,
        date: new Date().toISOString(),
      };

      saveAllSimulations(allSimulations);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la simulation:', error);
    }
  };

  const getSimulationHistory = () => {
    if (!exploitationSelected?.id) return [];

    try {
      const allSimulations = getAllSimulations();
      const exploitationData = allSimulations[exploitationSelected.id];
      return exploitationData?.history || [];
    } catch (error) {
      console.error('Erreur récupération historique', error);
    }
    return [];
  };

  /**
   * This function is used by
   *  - LastSimulate
   *  - CurrentSimulation from localStorage
   *  - <SelectSimuOtherUser />
   */
  const updateSimulationParams = (params, cultures = culturesResult) => {
    // PARCELLES
    if (params.parcels?.length > 0) {
      const availableParcelIds = exploitationSelected?.parcels?.map((p) => p.id) || [];
      const validParcels = params.parcels.filter((id) => availableParcelIds.includes(id));
      setSelectedParcels(validParcels);
    }

    // CULTURES
    if (params.cultures?.length > 0) {
      const validCultures = params.cultures.filter((c) => cultures.some((fc) => fc.id === c.id));

      setSelectedCultures(validCultures.map((c) => c.id));

      const minMaxData = Object.fromEntries(
        validCultures.map((culture) => [
          culture.id,
          {
            min: +culture?.minMax?.min || 0,
            max: +culture?.minMax?.max || 0,
            unit: culture?.minMax?.unit || 'ha',
          },
        ]),
      );
      setCultureMinMax(minMaxData);
    }

    // CONSTRAINTS (co-products, irrigation, etc.)
    if (params.constraints?.length > 0) {
      const firstConstraint = params.constraints[0];

      // Detect Old Format incoming
      const isOldFormat = typeof firstConstraint === 'object';

      if (isOldFormat) {
        // Old Format : [{ id, name, min, max, coProductFlag }, { nitrogen: true }, ...]
        const constraintNames = [];
        const constraintVals = {};

        params.constraints.forEach((constraint) => {
          if (constraint.name) {
            const constraintKey = constraint.name;
            constraintNames.push(constraintKey);
            constraintVals[constraintKey] = {
              min: constraint.min !== undefined ? constraint.min : '',
              max: constraint.max !== undefined ? constraint.max : '',
              hasInput: true,
              unit: constraint.unit || 'ha',
              ...(constraint.coProductFlag != null && { coProductFlag: constraint.coProductFlag }),
            };
          } else {
            const keys = Object.keys(constraint);
            keys.forEach((key) => {
              if (constraint[key] === true) {
                constraintNames.push(key);
                constraintVals[key] = { min: '', max: '', unit: 'ha' };
              }
            });
          }
        });

        setSelectedConstraints(constraintNames);
        setConstraintValues(constraintVals);
      } else {
        setSelectedConstraints(params.constraints);
        if (params.constraintValues) {
          setConstraintValues(params.constraintValues);
        }
      }
    } else if (params.constraintValues) {
      setConstraintValues(params.constraintValues);
    } else {
      setConstraintValues([]);
    }

    // Strategies
    if (params.strategies?.length > 0) {
      setSelectedStrategies(params.strategies);
    }

    if (params.parcelConstraints?.length > 0 || params.obligationRestriction?.length > 0) {
      if (params.obligationRestriction) {
        const transformedConstraints = params.obligationRestriction.map((constraint, index) => ({
          id: constraint.id || Date.now() + index,
          constraint: constraint.constraint,
          cultureType: constraint.cultureType || 'main',
          cultures: (constraint.Exploitation_Culture_Id || []).filter((id) =>
            cultures.some((fc) => fc.id === id),
          ),
          parcels: constraint.Parcel_Id || [],
          years:
            constraint.constraint === 'conversion'
              ? constraint.year
                ? [constraint.year]
                : []
              : constraint.years || [],
          name: constraint.name || '',
        }));
        setParcelConstraints(transformedConstraints);
      } else {
        setParcelConstraints(params.parcelConstraints);
      }
    } else {
      setParcelConstraints([]);
    }

    if (params.yearsToSimulate) {
      setYearsToSimulate(params.yearsToSimulate);
    }
    setIsSimulationContextUpdated(false);
  };

  const toggleSelection = (item, list, setList) => {
    // Group toggle: item is an array of ids (e.g. [parentId, child1, child2])
    if (Array.isArray(item)) {
      const parentId = item[0];
      const isSelected = list.includes(parentId);

      if (isSelected) {
        setList(list.filter((i) => !item.includes(i)));
      } else {
        const toAdd = item.filter((id) => !list.includes(id));
        setList([...list, ...toAdd]);
      }
      return;
    }

    // Single toggle
    if (list.includes(item)) {
      setList(list.filter((i) => i !== item));
    } else {
      setList([...list, item]);
    }
  };

  const toggleConstraintSelection = (constraintName, options = {}) => {
    if (selectedConstraints.includes(constraintName)) {
      setSelectedConstraints(selectedConstraints.filter((name) => name !== constraintName));
      setConstraintValues((prev) => {
        const newValues = { ...prev };
        delete newValues[constraintName];
        return newValues;
      });
    } else {
      setSelectedConstraints([...selectedConstraints, constraintName]);
      setConstraintValues((prev) => ({
        ...prev,
        [constraintName]: {
          min: '',
          max: '',
          ...(options.hasInput && { hasInput: true, unit: 'ha' }),
          ...(options.coProductFlag && { coProductFlag: true }),
        },
      }));
    }
  };

  const updateConstraintValue = (constraintName, field, value) => {
    setConstraintValues((prev) => ({
      ...prev,
      [constraintName]: {
        ...prev[constraintName],
        [field]: value,
      },
    }));
  };

  const toggleCultureSelection = (cultureId) => {
    if (selectedCultures.includes(cultureId)) {
      setSelectedCultures(selectedCultures.filter((id) => id !== cultureId));
      const newConstraints = { ...cultureMinMax };
      delete newConstraints[cultureId];
      setCultureMinMax(newConstraints);
    } else {
      setSelectedCultures([...selectedCultures, cultureId]);
      setCultureMinMax({
        ...cultureMinMax,
        [cultureId]: { min: 0, max: 0, unit: 'ha' },
      });
    }
  };

  const updateCultureMinMax = (cultureId, field, value) => {
    setCultureMinMax((prev) => ({
      ...prev,
      [cultureId]: {
        ...prev[cultureId],
        [field]: value,
      },
    }));
  };

  const updateConstraintField = (key, value) => {
    setCurrentConstraint((prev) => {
      return {
        ...prev,
        [key]: value,
        ...(key === 'cultureType' && { cultures: [] }),
        ...(key === 'constraint' && value !== 'obligation'
          ? { cultureType: 'main', cultures: [] }
          : { cultures: [] }),
        ...(key === 'constraint' && value === 'conversion' && { years: [] }),
      };
    });
  };

  const toggleParcelConstraintField = (field, itemId) => {
    setCurrentConstraint((prev) => {
      const currentList = prev[field] || [];

      // Direct set (array replacement)
      if (Array.isArray(itemId) && typeof itemId[0] !== 'number') {
        return { ...prev, [field]: itemId };
      }

      // Group toggle: array of ids from map click
      if (Array.isArray(itemId)) {
        const parentId = itemId[0];
        const isSelected = currentList.includes(parentId);

        const newList = isSelected
          ? currentList.filter((id) => !itemId.includes(id))
          : [...currentList, ...itemId.filter((id) => !currentList.includes(id))];
        return { ...prev, [field]: newList };
      }

      // Single toggle
      const newList = currentList.includes(itemId)
        ? currentList.filter((id) => id !== itemId)
        : [...currentList, itemId];
      return { ...prev, [field]: newList };
    });
  };

  const saveConstraint = () => {
    if (!currentConstraint.constraint) {
      toast.error(t('simulation.messages.selectConstraintType'));
      return false;
    }
    if (
      currentConstraint.constraint !== 'conversion' &&
      (!currentConstraint.cultures || currentConstraint.cultures.length === 0)
    ) {
      toast.error(t('simulation.messages.selectAtLeastOneCulture'));
      return false;
    }
    if (!currentConstraint.parcels || currentConstraint.parcels.length === 0) {
      toast.error(t('simulation.common.selectAtLeastOneParcel'));
      return false;
    }
    if (!currentConstraint.years || currentConstraint.years.length === 0) {
      toast.error(t('simulation.messages.selectAtLeastOneCampaign'));
      return false;
    }
    if (
      currentConstraint.constraint === 'obligation' ||
      currentConstraint.constraint === 'interdiction'
    ) {
      const existingConstraints = parcelConstraints.filter(
        (c) =>
          c.id !== currentConstraint.id &&
          (c.constraint === 'obligation' || c.constraint === 'interdiction'),
      );

      const duplicates = [];

      for (const parcelId of currentConstraint.parcels) {
        for (const year of currentConstraint.years) {
          for (const cultureId of currentConstraint.cultures) {
            const exists = existingConstraints.some(
              (c) =>
                c.parcels?.includes(parcelId) &&
                c.years?.includes(year) &&
                c.cultures?.includes(cultureId),
            );

            if (exists) {
              const parcelName =
                exploitationSelected?.parcels?.find((p) => p.id === parcelId)?.name || parcelId;
              const cultureName =
                culturesResult?.find((c) => c.id === cultureId)?.name || cultureId;
              duplicates.push(`${parcelName} / ${year} / ${cultureName}`);
            }
          }
        }
      }

      if (duplicates.length > 0) {
        toast.error(
          t('simulation.messages.duplicateConstraints', { constraints: duplicates.join('\n') }),
        );
        return false;
      }
    }

    if (currentConstraint.id) {
      setParcelConstraints((prev) =>
        prev.map((c) => (c.id === currentConstraint.id ? currentConstraint : c)),
      );
    } else {
      const newConstraint = { ...currentConstraint, id: Date.now() };
      setParcelConstraints((prev) => [...prev, newConstraint]);
    }

    toggleModal(false);
    return true;
  };

  const deleteConstraint = (id) => {
    setParcelConstraints((prev) => prev.filter((c) => c.id !== id));
  };

  const toggleModal = (valueBoolean, constraint = null) => {
    setShowConstraintModal(valueBoolean);
    if (!valueBoolean) {
      setCurrentConstraint({});
    } else if (constraint) {
      setCurrentConstraint(constraint);
    } else {
      setCurrentConstraint({
        constraint: 'obligation',
        cultureType: 'main',
        cultures: [],
        parcels: [],
        years: [],
      });
    }
  };

  const editConstraint = (constraint) => {
    toggleModal(true, constraint);
  };

  const normalize = (str) =>
    str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const filteredParcels = sortWithLocale(
    exploitationSelected?.parcels?.filter((parcel) =>
      normalize(parcel.name).includes(normalize(parcelSearchTerm)),
    ),
    'name',
  );

  const filteredCultures = culturesResult?.filter((culture) =>
    normalize(culture.name).includes(normalize(cultureSearchTerm)),
  );

  const buildSimulationConfig = () => {
    const cultures = selectedCultures.map((id) => {
      const culture = culturesResult?.find((c) => c.id === id);
      return {
        id: culture?.id,
        name: culture?.name,
        type: culture?.type,
        category: culture?.category,
        minMax: {
          ...cultureMinMax[id],
          min: cultureMinMax[id]?.min === '' ? 0 : cultureMinMax[id]?.min,
          max: cultureMinMax[id]?.max === '' ? 0 : cultureMinMax[id]?.max,
        },
      };
    });

    return {
      parcels: selectedParcels,
      cultures: cultures,
      constraints: selectedConstraints.map((nameConstraint) => {
        const constraintValue = constraintValues[nameConstraint];
        if (constraintValue?.hasInput) {
          return {
            name: nameConstraint,
            min: constraintValue.min === '' ? 0 : constraintValue.min,
            max: constraintValue.max === '' ? 0 : constraintValue.max,
            ...(constraintValue?.unit && { unit: constraintValue.unit }),
            ...(constraintValue?.coProductFlag != null && {
              coProductFlag: constraintValue.coProductFlag,
            }),
          };
        } else {
          return {
            [nameConstraint]: true,
          };
        }
      }),
      strategies: selectedStrategies,
      obligationRestriction: parcelConstraints.map((c) => ({
        constraint: c.constraint,
        cultureType: c.cultureType,
        Exploitation_Culture_Id: c.cultures,
        Parcel_Id: c.parcels,
        ...(c.constraint === 'conversion' ? { year: c.years?.[0] } : { years: c.years }),
        name: c.name || '',
      })),
      yearsToSimulate: yearsToSimulate,
      exploitationId: exploitationSelected?.id,
    };
  };

  const launchSimulation = async () => {
    setIsSimulating(true);
    setSimulationError(null);
    setJobId(null);

    const simulationConfig = buildSimulationConfig();

    try {
      const response = await simulate(exploitationSelected.id, simulationConfig);
      const jobId = response?.data?.data?.job_id;

      if (!jobId) throw new Error('No job ID received');
      setJobId(jobId);

      let status = null;
      while (true) {
        sendEvent('loaderFullScreen', { loaderNeeded: false });

        await new Promise((resolve) => setTimeout(resolve, 2000));
        const statusResponse = await getSimulationStatus(jobId);
        status = statusResponse.data.data.status;
        if (status === 'COMPLETE' || status === 'ERROR') break;
      }

      const result = await getSimulationResult(jobId);

      if (result) {
        sendEvent('loaderFullScreen', { loaderNeeded: true });

        onSimulationComplete({
          config: simulationConfig,
          result: result.data.data,
          jobId: jobId,
        });
        saveToLocalStorage(simulationConfig, result.data.data, jobId);
      }

      return { success: true, jobId };
    } catch (error) {
      setSimulationError(error?.response?.data || error.message);
    } finally {
      setIsSimulating(false);
    }
  };

  const clearSimulationError = () => {
    setSimulationError(null);
  };

  const getRecapData = () =>
    buildRecapData({
      parcelsIds: selectedParcels,
      culturesIds: selectedCultures,
      cultureMinMax,
      constraintsNames: selectedConstraints,
      constraintValues,
      strategies: selectedStrategies,
      parcelConstraints,
      exploitationParcels: exploitationSelected?.parcels,
      culturesResult,
      t,
    });
  const canProceedToStep = (step) => {
    switch (step) {
      case 1:
        return selectedParcels.length > 0;
      case 2:
        return selectedCultures.length >= 2;
      case 3:
        return true;
      case 4:
        return selectedStrategies.length > 0;
      case 5:
        return true;
      default:
        return true;
    }
  };

  return {
    selectedParcels,
    setSelectedParcels,
    selectedCultures,
    setSelectedCultures,
    cultureMinMax,
    setCultureMinMax,
    selectedConstraints,
    setSelectedConstraints,
    constraintValues,
    setConstraintValues,
    selectedStrategies,
    setSelectedStrategies,
    yearsToSimulate,
    setYearsToSimulate,
    parcelConstraints,
    setParcelConstraints,

    showConstraintModal,
    currentConstraint,

    parcelSearchTerm,
    setParcelSearchTerm,
    cultureSearchTerm,
    setCultureSearchTerm,
    viewMapParcels,
    setViewMapParcels,
    mapCoordinates,
    coProducts,

    filteredParcels,
    filteredCultures,
    culturesResult,
    fixedCultureIds,

    isSimulating,
    simulationError,
    jobId,

    updateSimulationParams,
    toggleSelection,
    toggleCultureSelection,
    updateCultureMinMax,
    updateConstraintField,
    toggleConstraintSelection,
    toggleParcelConstraintField,
    updateConstraintValue,
    saveConstraint,
    deleteConstraint,
    editConstraint,
    toggleModal,
    launchSimulation,
    buildSimulationConfig,
    getRecapData,
    canProceedToStep,
    getSimulationHistory,
    clearSimulationError,
    setHasExistingSimulation,
  };
};

export default useSimulationConfig;
